import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpStatusCode, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from 'src/app/toast/toast.service';
import { BinaryDataFragmentSyncDataDataService } from './binary-data-fragment-sync-data-data.service';

/**
 * This HTTP interceptor catches HTTP request errors for complete binary data sync requests when there is an image save error.
 * It deletes all binary metadata sync data related to service and shows a toast message to the user informing
 * them of the action taken. This allows the client to gracefully handle scenarios where there are issues saving image data,
 * as keeping the sync data on the device would result in continued failed sync attempts.
 */
@Injectable()
export class CompleteBinaryDataSyncInterceptor implements HttpInterceptor {

	/**
	 * @constructor
	 *
	 * @param toastService The toast service.
	 */
	constructor(
		private readonly toastService: ToastService,
	) { }

	/**
	 * Intercepts HTTP requests and handles image save errors for complete binary data sync requests.
	 * 
	 * @param request The outgoing request object to handle.
	 * @param next The next interceptor in the chain, or the back-end if no interceptors remain in the chain.
	 */
	public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(catchError((error: HttpErrorResponse) => {
			const binaryDataSyncRequest: string | null = request.headers.get(BinaryDataFragmentSyncDataDataService.COMPLETE_BINARY_DATA_SYNC_HEADER_NAME);

			// Only process errors for complete binary data sync requests
			if (!binaryDataSyncRequest) {
				return throwError(() => error);
			}

			// Handle 424 Failed Dependency errors with ImageSaveError
			if (error.status === HttpStatusCode.FailedDependency && error.error === "ImageSaveError") {
				const imageSaveErrorToastMessage: string = `Encountered error saving image data for the service with id: ${binaryDataSyncRequest}. Deleted all image sync data related to the service. Please click the refresh button on the Service Preview page to ensure your services are up to date.`;
				this.toastService.ShowError(imageSaveErrorToastMessage);
			}

			return throwError(() => error);
		}));
	}
}
